import { QueryFilter } from '@modules/queryBuilder/components/QueryFilter/QueryFilter';
import { QueryFilterActions } from '@modules/queryBuilder/components/QueryFilterActions';
import { QueryFilterConjunctionSelectButton } from '@modules/queryBuilder/components/QueryFilterConjunctionSelectButton';
import { QueryFilterSelect } from '@modules/queryBuilder/components/QueryFilterSelect';
import {
  getNewFilterNode,
  removeQueryFilterNode,
  upsertQueryFilterNode,
} from '@modules/queryBuilder/helpers';
import { QueryBuilderActionType } from '@modules/queryBuilder/store/queryBuilderActionType';
import { useQueryBuilderStore } from '@modules/queryBuilder/store/useQueryBuilderStore';
import { Paper, Stack } from '@mui/material';
import { NormalisedFilterGroup } from 'v2/querybuilder/types/querystore';

interface QueryFilterGroupProps {
  filterGroup: NormalisedFilterGroup;
  skipBackground?: boolean;
}

export function QueryFilterGroup({
  filterGroup,
  skipBackground = false,
}: QueryFilterGroupProps) {
  const nodes = useQueryBuilderStore(({ query }) => query.nodes);
  const rootID = useQueryBuilderStore(({ query }) => query.rootID);
  const dispatch = useQueryBuilderStore(({ dispatch }) => dispatch);

  const renderFilters = () => {
    return filterGroup.children.map((filterID) => {
      const filterNode = nodes[filterID];

      if (filterNode.type === 'group') {
        return (
          <QueryFilterGroup key={filterNode.id} filterGroup={filterNode} />
        );
      }

      if (filterNode.type === 'filter' && filterNode.field) {
        return <QueryFilter key={filterNode.id} filter={filterNode} />;
      }

      return (
        <QueryFilterSelect
          key={`${filterNode.id}_filter_select`}
          parentID={filterNode.parent ?? rootID}
          filter={filterNode}
        />
      );
    });
  };

  const removeFilter = () => {
    const updatedNodes = removeQueryFilterNode({
      node: { ...filterGroup },
      nodes,
      parentID: filterGroup.parent,
    });

    dispatch({
      type: QueryBuilderActionType.SET_QUERY,
      payload: {
        nodes: updatedNodes,
      },
    });
  };

  const addNestedFilter = () => {
    const newFilterNode = getNewFilterNode({ parentID: filterGroup.id });

    const nodesWithNewFilter = upsertQueryFilterNode({
      node: newFilterNode,
      nodesByID: nodes,
      parentID: filterGroup.id,
    });

    dispatch({
      type: QueryBuilderActionType.SET_QUERY,
      payload: {
        nodes: nodesWithNewFilter,
      },
    });

    dispatch({
      type: QueryBuilderActionType.SET_METADATA,
      payload: {
        filterIDUnderInspect: newFilterNode.id,
      },
    });
  };

  const parentNode = filterGroup.parent && nodes[filterGroup.parent];
  const isFirstChild = parentNode
    ? parentNode?.children?.[0] === filterGroup.id
    : true;
  const shouldShowConjuction =
    parentNode && parentNode.type === 'group' && !isFirstChild;

  return (
    <Stack
      direction="row"
      spacing={1}
      alignItems="center"
      sx={{
        [`.filter-actions-${filterGroup.id}`]: {
          visibility: 'hidden',
        },
        '&:hover': {
          [`.filter-actions-${filterGroup.id}`]: {
            visibility: 'visible',
          },
        },
      }}
    >
      {shouldShowConjuction && (
        <QueryFilterConjunctionSelectButton filter={parentNode} />
      )}
      <Stack
        {...(skipBackground ? {} : { component: Paper, variant: 'outlined' })}
        sx={{
          border: '1px solid',
          borderColor: (theme) => theme.palette.divider,
          borderRadius: 2,
          p: 2,
        }}
        direction="column"
        spacing={1}
      >
        {renderFilters()}
      </Stack>
      <QueryFilterActions
        id={filterGroup.id}
        onAddNestedFilter={addNestedFilter}
        onRemoveFilter={removeFilter}
      />
    </Stack>
  );
}
